// import { useStore } from '@tanstack/react-form'; // KALDIRILDI
import React, { useCallback } from 'react';
import { AddLabour } from './Dialogs/AddLabour';
import { AddMaterial } from './Dialogs/AddMaterial';
import { AddDefect } from './Dialogs/AddDefect';
import { AddTransport } from './Dialogs/AddTransport';
import { MaterialStocks } from './Dialogs/MaterialStocks';
import { useDialog } from '../../../../contexts/DialogContext';

function useFormValues(form, selector) {
  const [val, setVal] = React.useState(() => selector(form.values));
  React.useEffect(() => {
    const unsub = form.subscribe(() => setVal(selector(form.values)));
    return unsub;
  }, [form, selector]);
  return val;
}

export function useDefectDialogHandlers({ form, currency, tanentCode }) {
    const { openDialog } = useDialog();

    const {
        Defects,
        DefectLevels,
        Labours,
        Materials,
        WorkOrderId,
        WorkOrderDetails,
        WorkOrderLabours,
        WorkOrderMaterials
    } = useFormValues(form, v => ({
      Defects: v.Defects || [],
      DefectLevels: v.DefectLevels || [],
      Labours: v.Labours || [],
      Materials: v.Materials || [],
      WorkOrderId: v.WorkOrderId,
      WorkOrderDetails: v.WorkOrderDetails || [],
      WorkOrderLabours: v.WorkOrderLabours || [],
      WorkOrderMaterials: v.WorkOrderMaterials || []
    }));

    const handleAddTransport = useCallback(() => {
        openDialog(() => <AddTransport />);
    }, [openDialog]);

    const handleShowMaterialStocks = useCallback(() => {
        openDialog(() => <MaterialStocks />);
    }, [openDialog]);

    const handleAddDefect = useCallback(() => {
        const initialDefectIds = WorkOrderDetails.map(d => d.DefectId);
        openDialog(({ closeDialog }) => (
            <AddDefect
                initialDefectIds={initialDefectIds}
                defectLevels={DefectLevels}
                defects={Defects}
                onAbort={closeDialog}
                onDone={selectedDefectIds => {
                    closeDialog();
                    const removedDefectIds = initialDefectIds.filter(id => !selectedDefectIds.includes(id));
                    let workOrderDetails = form.getFieldValue('WorkOrderDetails') || [];

                    if (removedDefectIds.length) {
                        let workOrderLabours = form.getFieldValue('WorkOrderLabours') || [];
                        let workOrderMaterials = form.getFieldValue('WorkOrderMaterials') || [];
                        for (const removedDefectId of removedDefectIds) {
                            workOrderDetails = workOrderDetails.filter(d => d.DefectId !== removedDefectId);
                            workOrderLabours = workOrderLabours.filter(l => l.DefectId !== removedDefectId);
                            workOrderMaterials = workOrderMaterials.filter(m => m.DefectId !== removedDefectId);
                        }
                        form.setFieldValue('WorkOrderDetails', workOrderDetails);
                        form.setFieldValue('WorkOrderLabours', workOrderLabours);
                        form.setFieldValue('WorkOrderMaterials', workOrderMaterials);
                    }

                    const remainingWorkOrderDefects = workOrderDetails.filter(
                        detail => !removedDefectIds.includes(detail.DefectId)
                    );
                    const newlyAddedDefectIds = selectedDefectIds.filter(id => !initialDefectIds.includes(id));
                    const newlyAddedDefects = Defects.filter(defect => newlyAddedDefectIds.includes(defect.Code));
                    const newWorkOrderDefects = newlyAddedDefects.map(defect => ({
                        ...defect,
                        WorkOrderId: WorkOrderId,
                        DefectId: defect.Code
                    }));
                    form.setFieldValue('WorkOrderDetails', [...remainingWorkOrderDefects, ...newWorkOrderDefects]);
                }}
            />
        ));
    }, [openDialog, WorkOrderDetails, DefectLevels, Defects, WorkOrderId, form]);

    const handleAddLabour = useCallback(
        defect => {
            const initialSelectedLabourIds = defect.labours.map(l => l.LabourId);
            openDialog(({ closeDialog }) => (
                <AddLabour
                    labours={Labours}
                    initialSelectedLabourIds={initialSelectedLabourIds}
                    currency={currency}
                    tanentCode={tanentCode}
                    onAbort={closeDialog}
                    onDone={selectedLabourIds => {
                        closeDialog();
                        const removedLabourIds = initialSelectedLabourIds.filter(id => !selectedLabourIds.includes(id));
                        const remainingWorkOrderLabours = WorkOrderLabours.filter(
                            labour => !removedLabourIds.includes(labour.LabourId) || labour.DefectId !== defect.DefectId
                        );
                        const newlyAddedLabourIds = selectedLabourIds.filter(
                            id => !initialSelectedLabourIds.includes(id)
                        );
                        const newlyAddedLabours = Labours.filter(labour =>
                            newlyAddedLabourIds.includes(labour.LabourId)
                        );
                        const newWorkOrderLabours = newlyAddedLabours.map(labour => ({
                            ...labour,
                            DefectId: defect.DefectId
                        }));
                        form.setFieldValue('WorkOrderLabours', [...remainingWorkOrderLabours, ...newWorkOrderLabours]);
                    }}
                />
            ));
        },
        [form, Labours, WorkOrderLabours, openDialog, currency, tanentCode]
    );

    const handleAddMaterial = useCallback(
        defect => {
            const initialSelectedMaterialsIds = defect.materials.map(m => m.MaterialId);
            openDialog(({ closeDialog }) => (
                <AddMaterial
                    materials={Materials}
                    initialSelectedMaterialsIds={initialSelectedMaterialsIds}
                    initalMaterialCounts={defect.materials.reduce((acc, curr) => {
                        acc[curr.MaterialId] = curr.Count;
                        return acc;
                    }, {})}
                    currency={currency}
                    tanentCode={tanentCode}
                    onAbort={closeDialog}
                    onDone={selectedMaterials => {
                        closeDialog();
                        const removedMaterialIds = initialSelectedMaterialsIds.filter(
                            id => !selectedMaterials.map(m => m.MaterialId).includes(id)
                        );
                        const remainingWorkOrderMaterials = WorkOrderMaterials.filter(
                            material =>
                                !removedMaterialIds.includes(material.MaterialId) ||
                                material.DefectId !== defect.DefectId
                        );
                        const newlyAddedMaterialIds = selectedMaterials
                            .map(m => m.MaterialId)
                            .filter(id => !initialSelectedMaterialsIds.includes(id));
                        const newlyAddedMaterials = Materials.filter(material =>
                            newlyAddedMaterialIds.includes(material.MaterialId)
                        );
                        const newWorkOrderMaterials = newlyAddedMaterials.map(material => ({
                            ...material,
                            DefectId: defect.DefectId,
                            Count: selectedMaterials.find(m => m.MaterialId === material.MaterialId)?.Count || 1
                        }));
                        form.setFieldValue('WorkOrderMaterials', [
                            ...remainingWorkOrderMaterials,
                            ...newWorkOrderMaterials
                        ]);
                    }}
                />
            ));
        },
        [form, Materials, WorkOrderMaterials, openDialog, currency, tanentCode]
    );

    const handleDeleteLabour = useCallback(
        labour => {
            const updated = WorkOrderLabours.filter(
                l => l.LabourId !== labour.LabourId || l.DefectId !== labour.DefectId
            );
            form.setFieldValue('WorkOrderLabours', updated);
        },
        [WorkOrderLabours, form]
    );

    const handleDeleteMaterial = useCallback(
        material => {
            const updated = WorkOrderMaterials.filter(
                m => m.MaterialId !== material.MaterialId || m.DefectId !== material.DefectId
            );
            form.setFieldValue('WorkOrderMaterials', updated);
        },
        [WorkOrderMaterials, form]
    );

    return {
        handleAddTransport,
        handleShowMaterialStocks,
        handleAddDefect,
        handleAddLabour,
        handleAddMaterial,
        handleDeleteLabour,
        handleDeleteMaterial
    };
}